const STORAGE_PREFIX = "shiftcanvas";

// Session keys - cleared on user change/logout (sensitive user data)
const SESSION_KEYS = [
  "view_mode",
];

// Preference keys - preserved per user (settings/preferences, not sensitive).
// The last-used site is a per-user PREFERENCE, not session state: the product
// rule is "returning users come back to the same org as last time", so logout
// must not wipe it. Safe because these keys are user-scoped — user B never
// inherits user A's selection. clearAllUserData() still removes everything.
const PREFERENCE_KEYS = [
  "personalization",
  "notifications",
  "fcm_token",
  "selected_site_id",
  "selected_site_name",
];

const LEGACY_KEYS = [
  "shiftcanvas_selected_site_id",
  "shiftcanvas_view_mode",
  "shiftcanvas_selected_site_name",
  "shiftcanvas_personalization",
  "shiftcanvas_notifications",
  "shiftcanvas_fcm_token",
];

function getUserScopedKey(baseKey: string, userId: string | null): string {
  if (!userId) {
    return `${STORAGE_PREFIX}_${baseKey}`;
  }
  return `${STORAGE_PREFIX}_${userId}_${baseKey}`;
}

export function getUserStorage(userId: string | null) {
  return {
    getSiteId: (): string | null => {
      try {
        return localStorage.getItem(getUserScopedKey("selected_site_id", userId));
      } catch (e) {
        console.warn("[UserStorage] Failed to get site ID:", e);
        return null;
      }
    },

    setSiteId: (siteId: string): void => {
      try {
        localStorage.setItem(getUserScopedKey("selected_site_id", userId), siteId);
      } catch (e) {
        console.warn("[UserStorage] Failed to set site ID:", e);
      }
    },

    clearSiteId: (): void => {
      try {
        localStorage.removeItem(getUserScopedKey("selected_site_id", userId));
      } catch (e) {
        console.warn("[UserStorage] Failed to clear site ID:", e);
      }
    },

    getViewMode: (): "staff" | "manager" => {
      try {
        const stored = localStorage.getItem(getUserScopedKey("view_mode", userId));
        if (stored === "manager" || stored === "staff") {
          return stored;
        }
      } catch (e) {
        console.warn("[UserStorage] Failed to get view mode:", e);
      }
      return "staff";
    },

    setViewMode: (mode: "staff" | "manager"): void => {
      try {
        localStorage.setItem(getUserScopedKey("view_mode", userId), mode);
      } catch (e) {
        console.warn("[UserStorage] Failed to set view mode:", e);
      }
    },

    getSiteName: (): string | null => {
      try {
        return localStorage.getItem(getUserScopedKey("selected_site_name", userId));
      } catch (e) {
        console.warn("[UserStorage] Failed to get site name:", e);
        return null;
      }
    },

    setSiteName: (name: string): void => {
      try {
        localStorage.setItem(getUserScopedKey("selected_site_name", userId), name);
      } catch (e) {
        console.warn("[UserStorage] Failed to set site name:", e);
      }
    },

    clearAll: (): void => {
      try {
        for (const key of SESSION_KEYS) {
          localStorage.removeItem(getUserScopedKey(key, userId));
        }
      } catch (e) {
        console.warn("[UserStorage] Failed to clear storage:", e);
      }
    },
    
    // Preference getters/setters - user-scoped but preserved across sessions
    getPersonalization: (): string | null => {
      try {
        return localStorage.getItem(getUserScopedKey("personalization", userId));
      } catch (e) {
        return null;
      }
    },
    
    setPersonalization: (data: string): void => {
      try {
        localStorage.setItem(getUserScopedKey("personalization", userId), data);
      } catch (e) {
        console.warn("[UserStorage] Failed to set personalization:", e);
      }
    },
    
    getNotifications: (): string | null => {
      try {
        return localStorage.getItem(getUserScopedKey("notifications", userId));
      } catch (e) {
        return null;
      }
    },
    
    setNotifications: (data: string): void => {
      try {
        localStorage.setItem(getUserScopedKey("notifications", userId), data);
      } catch (e) {
        console.warn("[UserStorage] Failed to set notifications:", e);
      }
    },
    
    getFcmToken: (): string | null => {
      try {
        return localStorage.getItem(getUserScopedKey("fcm_token", userId));
      } catch (e) {
        return null;
      }
    },
    
    setFcmToken: (token: string): void => {
      try {
        localStorage.setItem(getUserScopedKey("fcm_token", userId), token);
      } catch (e) {
        console.warn("[UserStorage] Failed to set FCM token:", e);
      }
    },
  };
}

// Clear session data only (site selection, view mode) - called on user change/logout
// Preferences (personalization, notifications, fcm_token) are preserved per-user
export function clearSessionData(userId: string | null): void {
  try {
    // Clear legacy non-scoped keys — these predate user scoping, so they CAN
    // leak across users and must always go (including the old site selection).
    localStorage.removeItem("shiftcanvas_selected_site_id");
    localStorage.removeItem("shiftcanvas_view_mode");
    localStorage.removeItem("shiftcanvas_selected_site_name");

    // Clear user-scoped session keys for this user. Per-user PREFERENCE_KEYS
    // (including the last-used site) deliberately survive logout.
    if (userId) {
      for (const key of SESSION_KEYS) {
        localStorage.removeItem(getUserScopedKey(key, userId));
      }
    }

    // Also clear any session/site keys stored without userId scope — unscoped
    // values are not attributable to a user, so they never survive.
    for (const key of [...SESSION_KEYS, "selected_site_id", "selected_site_name"]) {
      localStorage.removeItem(`${STORAGE_PREFIX}_${key}`);
    }

    console.log("[UserStorage] Cleared session data from localStorage");
  } catch (e) {
    console.warn("[UserStorage] Failed to clear session data:", e);
  }
}

// DEPRECATED: Only use for complete data wipe (e.g., app uninstall scenario)
// For normal logout/user switch, use clearSessionData() instead
export function clearAllUserData(): void {
  try {
    for (const key of LEGACY_KEYS) {
      localStorage.removeItem(key);
    }
    
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    
    console.log("[UserStorage] Cleared all user data from localStorage");
  } catch (e) {
    console.warn("[UserStorage] Failed to clear all user data:", e);
  }
}

export function clearLegacyStorage(): void {
  try {
    for (const key of LEGACY_KEYS) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn("[UserStorage] Failed to clear legacy storage:", e);
  }
}

export function getLastUserId(): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}_last_user_id`);
  } catch (e) {
    return null;
  }
}

export function setLastUserId(userId: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}_last_user_id`, userId);
  } catch (e) {
    console.warn("[UserStorage] Failed to set last user ID:", e);
  }
}
